import { useEffect, useState, useCallback, useMemo } from 'react'
import { useForm } from 'react-hook-form'
import { zodResolver } from '@hookform/resolvers/zod'
import { z } from 'zod'
import { useAuth } from '../../hooks/useAuth'
import { useCreateNutritionPreset, useUpdateNutritionPreset } from '../../hooks/useNutritionPresets'
import { NutritionPreset } from '../../types'
import {
  Dialog,
  DialogContent,
  DialogHeader,
  DialogTitle,
} from '../ui/dialog'
import { Button } from '../ui/button'
import { Input } from '../ui/input'
import { Label } from '../ui/label'

const presetSchema = z.object({
  name: z.string().min(1, 'Name is required').max(100, 'Name too long'),
  carbs_g: z.number().min(0).optional(),
  fluids_ml: z.number().min(0).optional(),
  sodium_mg: z.number().min(0).optional(),
  caffeine_mg: z.number().min(0).optional(),
}).refine(
  (data) => data.carbs_g || data.fluids_ml || data.sodium_mg || data.caffeine_mg,
  {
    message: "At least one nutrition field must be provided",
    path: ["carbs_g"]
  }
)

type PresetFormData = z.infer<typeof presetSchema>

interface PresetFormProps {
  open: boolean
  onOpenChange: (open: boolean) => void
  preset?: NutritionPreset | null
}

export function PresetForm({ open, onOpenChange, preset }: PresetFormProps) {
  const [error, setError] = useState<string | null>(null)
  const { user } = useAuth()
  const createMutation = useCreateNutritionPreset()
  const updateMutation = useUpdateNutritionPreset()
  
  const isEditing = !!preset?.id
  const isLoading = createMutation.isPending || updateMutation.isPending
  
  const {
    register,
    handleSubmit,
    reset,
    formState: { errors }
  } = useForm<PresetFormData>({
    resolver: zodResolver(presetSchema),
    defaultValues: {
      name: '',
      carbs_g: undefined,
      fluids_ml: undefined,
      sodium_mg: undefined,
      caffeine_mg: undefined
    }
  })

  const defaultFormValues = useMemo(() => ({
    name: '',
    carbs_g: undefined,
    fluids_ml: undefined,
    sodium_mg: undefined,
    caffeine_mg: undefined
  }), [])

  // Reset form when dialog opens/closes or preset changes
  useEffect(() => {
    if (!open) return
    
    const formValues = preset ? {
      name: preset.name,
      carbs_g: preset.carbs_g || undefined,
      fluids_ml: preset.fluids_ml || undefined,
      sodium_mg: preset.sodium_mg || undefined,
      caffeine_mg: preset.caffeine_mg || undefined
    } : defaultFormValues
    
    reset(formValues)
    setError(null)
  }, [open, preset, reset, defaultFormValues])

  const onSubmit = useCallback(async (data: PresetFormData) => {
    if (!user?.id) {
      setError('User authentication required')
      return
    }

    setError(null)

    try {
      const nutritionData = {
        name: data.name.trim(),
        carbs_g: data.carbs_g || null,
        fluids_ml: data.fluids_ml || null,
        sodium_mg: data.sodium_mg || null,
        caffeine_mg: data.caffeine_mg || null,
      }

      if (isEditing && preset?.id) {
        await updateMutation.mutateAsync({
          id: preset.id,
          updates: nutritionData
        })
      } else {
        await createMutation.mutateAsync({
          user_id: user.id,
          ...nutritionData,
        })
      }

      onOpenChange(false)
      reset(defaultFormValues)
    } catch (error) {
      console.error('Failed to save preset:', error)
      setError(
        error instanceof Error 
          ? error.message 
          : `Failed to ${isEditing ? 'update' : 'create'} preset`
      )
    }
  }, [user?.id, isEditing, preset?.id, updateMutation, createMutation, onOpenChange, reset, defaultFormValues])

  const handleOpenChange = useCallback((newOpen: boolean) => {
    if (!newOpen) {
      reset(defaultFormValues)
      setError(null)
    }
    onOpenChange(newOpen)
  }, [onOpenChange, reset, defaultFormValues])

  return (
    <Dialog open={open} onOpenChange={handleOpenChange}>
      <DialogContent className="sm:max-w-[500px]">
        <DialogHeader>
          <DialogTitle>
            {isEditing ? 'Edit Preset' : 'Add New Preset'}
          </DialogTitle>
        </DialogHeader>
        
        {error && (
          <div className="text-sm text-red-600 bg-red-50 border border-red-200 rounded-md p-3 mb-4">
            {error}
          </div>
        )}
        
        <form onSubmit={handleSubmit(onSubmit)} className="space-y-4" noValidate>
          <div className="space-y-2">
            <Label htmlFor="name">Name</Label>
            <Input
              id="name"
              {...register('name')}
              placeholder="e.g., Maurten Gel 100, Soft Flask 500ml"
              disabled={isLoading}
              aria-describedby={errors.name ? 'name-error' : undefined}
            />
            {errors.name && (
              <p id="name-error" className="text-sm text-red-500" role="alert">
                {errors.name.message}
              </p>
            )}
          </div>

          <div className="grid grid-cols-2 gap-4">
            <div className="space-y-2">
              <Label htmlFor="carbs_g">Carbohydrates (g)</Label>
              <Input
                id="carbs_g"
                type="number"
                step="0.1"
                min="0"
                {...register('carbs_g', { 
                  valueAsNumber: true,
                  setValueAs: (value) => value === '' || isNaN(value) ? undefined : value
                })}
                placeholder="25"
                disabled={isLoading}
              />
            </div>

            <div className="space-y-2">
              <Label htmlFor="fluids_ml">Fluids (ml)</Label>
              <Input
                id="fluids_ml"
                type="number"
                min="0"
                {...register('fluids_ml', { 
                  valueAsNumber: true,
                  setValueAs: (value) => value === '' || isNaN(value) ? undefined : value
                })}
                placeholder="500"
                disabled={isLoading}
              />
            </div>
          </div>

          <div className="grid grid-cols-2 gap-4">
            <div className="space-y-2">
              <Label htmlFor="sodium_mg">Sodium (mg)</Label>
              <Input
                id="sodium_mg"
                type="number"
                min="0"
                {...register('sodium_mg', { 
                  valueAsNumber: true,
                  setValueAs: (value) => value === '' || isNaN(value) ? undefined : value
                })}
                placeholder="200"
                disabled={isLoading}
              />
            </div>

            <div className="space-y-2">
              <Label htmlFor="caffeine_mg">Caffeine (mg)</Label>
              <Input
                id="caffeine_mg"
                type="number"
                min="0"
                {...register('caffeine_mg', { 
                  valueAsNumber: true,
                  setValueAs: (value) => value === '' || isNaN(value) ? undefined : value
                })}
                placeholder="50"
                disabled={isLoading}
              />
            </div>
          </div>

          {errors.carbs_g && (
            <div className="text-sm text-red-600 bg-red-50 border border-red-200 rounded-md p-2" role="alert">
              {errors.carbs_g.message}
            </div>
          )}

          <div className="flex justify-end space-x-2">
            <Button
              type="button"
              variant="outline"
              onClick={() => onOpenChange(false)}
              disabled={isLoading}
            >
              Cancel
            </Button>
            <Button
              type="submit"
              disabled={isLoading}
            >
              {isLoading ? 'Saving...' : (isEditing ? 'Update Preset' : 'Add Preset')}
            </Button>
          </div>
        </form>
      </DialogContent>
    </Dialog>
  )
}